import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, FileText, Euro, Layers, Wrench, Square, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    total_quotes: 0,
    pending_quotes: 0,
    active_users: 0,
    total_revenue: 0
  });
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [validatedOrders, setValidatedOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, quotesRes] = await Promise.all([
        axios.get(`${API_URL}/stats`),
        axios.get(`${API_URL}/quotes`)
      ]);
      setStats(statsRes.data);
      
      const allQuotes = quotesRes.data;
      setRecentQuotes(allQuotes.slice(0, 5));
      
      // Filter validated orders (status "validé")
      const validated = allQuotes.filter(q => q.status === 'validé');
      setValidatedOrders(validated);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: FileText, label: "Total devis", value: stats.total_quotes, color: "#D4AF37" },
    { icon: Clock, label: "En attente", value: stats.pending_quotes, color: "#F59E0B" },
    { icon: Users, label: "Clients Pro actifs", value: stats.active_users, color: "#10B981" },
    { icon: Euro, label: "CA validé", value: `${stats.total_revenue?.toLocaleString('fr-FR')} €`, color: "#8B5CF6" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">Administration</h1>
        <p className="text-white/50 mt-1">Vue d'ensemble de l'activité</p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card-dashboard"
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20`, border: `1px solid ${stat.color}30` }}
              >
                <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-white/50 text-sm">{stat.label}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Validated Orders Section */}
      {validatedOrders.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-dashboard border-green-500/30"
          data-testid="validated-orders-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-green-500/10 border border-green-500/20">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Commandes validées</h2>
              <p className="text-white/50 text-sm">{validatedOrders.length} commande(s) à traiter</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {validatedOrders.map((order) => (
              <Link
                key={order.id}
                to={`/admin/devis/${order.id}`}
                className="flex items-center justify-between py-3 px-4 bg-[#0A0A0A] border border-green-500/20 hover:border-green-500/40 transition-colors group"
                data-testid={`validated-order-${order.id}`}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-white font-medium">{order.quote_number}</p>
                    <p className="text-white/50 text-xs">{order.client_info?.name} • {order.pro_company_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-green-400 font-semibold">{order.total_ttc?.toLocaleString('fr-FR')} €</p>
                    <p className="text-white/40 text-xs">{order.material_name}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Quotes */}
        <div className="card-dashboard">
          <h2 className="text-lg font-semibold text-white mb-4">Derniers devis</h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : recentQuotes.length === 0 ? (
            <p className="text-white/40 text-center py-8">Aucun devis</p>
          ) : (
            <div className="space-y-3">
              {recentQuotes.map((quote) => (
                <Link 
                  key={quote.id} 
                  to={`/admin/devis/${quote.id}`}
                  className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/5 -mx-2 px-2 transition-colors"
                >
                  <div>
                    <p className="text-white font-medium">{quote.quote_number}</p>
                    <p className="text-white/50 text-xs">{quote.pro_company_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{quote.total_ttc?.toLocaleString('fr-FR')} €</p>
                    <span className={`status-${quote.status} px-2 py-0.5 text-xs`}>
                      {quote.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="card-dashboard">
          <h2 className="text-lg font-semibold text-white mb-4">Gestion rapide</h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Layers, label: "Matières", path: "/admin/materiaux" },
              { icon: Wrench, label: "Façonnage", path: "/admin/faconnage" },
              { icon: Square, label: "Chants", path: "/admin/chants" },
              { icon: Users, label: "Utilisateurs", path: "/admin/utilisateurs" },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-white/10 hover:border-[#D4AF37]/50 transition-colors"
              >
                <item.icon className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white text-sm">{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
